import { Strategy } from "src/common/Strategy";
import { zUid } from "src/types/zUid";
import { z, ZodType } from "zod";
import { zSortDirection } from "../ManagementPage/Obj2Tab/ObjTableSettings/ObjTableSettingsTypes";
import { zEntityFilterValue } from "./AttsTree/types";
import { EntityFilters } from "./EntityList/types";

const zHierarchicalFilter = z.object({
  filters: z
    .object({
      attrId: zUid,
      filterValue: zEntityFilterValue.optional(),
    })
    .array(),
  searchValue: z.string().array(),
});

const zCommonEntityFilters = z.object({
  objectId: zUid.optional(),
  dictionaryValueIds: z.array(z.array(zUid)).optional(),
  query: z.string().optional(),
  restrictionValues: z.string().array().optional(),
  attributeId: zUid.optional(),
  exclusionEntityIds: zUid.array().optional(),
});

const zEntityFilters: ZodType<EntityFilters> = zCommonEntityFilters.extend({
  stateIds: zUid.array().optional(),
  hierarchicalFilter: zHierarchicalFilter.optional(),
});

const zLegacyEntityFilters = zCommonEntityFilters.extend({
  stateId: zUid.nullish(),
  hierarchicalFilter: zHierarchicalFilter.optional(),
});

const zLegacyEntityFiltersV1 = zLegacyEntityFilters.extend({
  hierarchicalFilter: zHierarchicalFilter
    .extend({ searchValue: z.string().or(z.string().array()) })
    .optional(),
});

enum Versions {
  DEPRECATED_1 = "1",
  DEPRECATED_2 = "2",
  LATEST = "3",
}

const zCommonEntityTSettingsData = z.object({
  page: z.number(),
  pageSize: z.number(),
  sort: z.string().optional(),
  sortOrder: zSortDirection.optional(),
});

const zEntityTSettingsDataV1 = zCommonEntityTSettingsData.extend({
  filters: zLegacyEntityFiltersV1.optional(),
});
export type ZEntityTSettingsDataV1 = z.infer<typeof zEntityTSettingsDataV1>;

const zEntityTSettingsV1 = z.object({
  version: z.literal(Versions.DEPRECATED_1),
  data: zEntityTSettingsDataV1,
});
const zEntityTSettingsV2 = z.object({
  version: z.literal(Versions.DEPRECATED_2),
  data: zCommonEntityTSettingsData.extend({
    filters: zLegacyEntityFilters.optional(),
  }),
});
const zEntityTSettingsV3 = z.object({
  version: z.literal(Versions.LATEST),
  data: zCommonEntityTSettingsData.extend({
    filters: zEntityFilters.optional(),
  }),
});

const zStoredEntityTSettings = z.discriminatedUnion("version", [
  zEntityTSettingsV1,
  zEntityTSettingsV2,
  zEntityTSettingsV3,
]);

// ConfigManager consumers always receive the current runtime shape.
export type ZEntityTSettings = z.infer<typeof zEntityTSettingsV3>;

export const getLatestEntityTSettings = (
  params: ZEntityTSettings["data"],
): ZEntityTSettings =>
  zEntityTSettingsV3.parse({
    version: Versions.LATEST,
    data: params,
  });

const migrateLegacySettings = (
  data:
    | z.infer<typeof zEntityTSettingsV1>["data"]
    | z.infer<typeof zEntityTSettingsV2>["data"],
): ZEntityTSettings => {
  const { filters: savedFilters, ...params } = data;
  if (!savedFilters) return getLatestEntityTSettings(params);

  const { stateId, hierarchicalFilter, ...filters } = savedFilters;
  return getLatestEntityTSettings({
    ...params,
    filters: {
      ...filters,
      stateIds: stateId == null || stateId === "" ? [] : [stateId],
      ...(hierarchicalFilter && {
        hierarchicalFilter: {
          ...hierarchicalFilter,
          // V1 preferences can still contain a scalar search value.
          searchValue:
            typeof hierarchicalFilter.searchValue === "string"
              ? [hierarchicalFilter.searchValue]
              : hierarchicalFilter.searchValue,
        },
      }),
    },
  });
};

export class EntityTableStrategy implements Strategy<ZEntityTSettings> {
  settings: ZEntityTSettings | null = null;

  bind(params?: unknown) {
    if (!params) return;
    const { version, data } = zStoredEntityTSettings.parse(params);
    this.settings =
      version === Versions.LATEST
        ? getLatestEntityTSettings(data)
        : migrateLegacySettings(data);
  }

  execute(): ZEntityTSettings {
    return (
      this.settings ??
      getLatestEntityTSettings({
        page: 0,
        pageSize: 10,
      })
    );
  }
}
